import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import svmpBanner from '../assets/svmp-banner.png';
import m7Banner from '../assets/m7-banner.png';
import './Blog.css';

type BlogPostBlock =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string }
  | { type: 'h3'; text: string }
  | { type: 'ul'; items: string[] }
  | { type: 'image'; src: string; alt: string; variant?: 'full-width' | 'contain' };

type BlogPost = {
  slug: string;
  title: string;
  author: string;
  category: string;
  coverImageSrc: string;
  coverImageAlt: string;
  excerpt: string;
  content: BlogPostBlock[];
};

type TocItem = { id: string; text: string; level: 2 | 3 };

const toHeadingId = (text: string) => {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
};

type BlogPostBlockWithId =
  | { type: 'p'; text: string }
  | { type: 'h2'; text: string; id: string }
  | { type: 'h3'; text: string; id: string }
  | { type: 'ul'; items: string[] }
  | { type: 'image'; src: string; alt: string; variant?: 'full-width' | 'contain' };

const normalizeContent = (content: BlogPostBlock[]) => {
  const items: TocItem[] = [];
  const blocks: BlogPostBlockWithId[] = [];
  const seen = new Map<string, number>();
  for (const block of content) {
    if (block.type === 'h2' || block.type === 'h3') {
      const base = toHeadingId(block.text);
      const nextCount = (seen.get(base) ?? 0) + 1;
      seen.set(base, nextCount);
      const id = nextCount === 1 ? base : `${base}-${nextCount}`;
      items.push({ id, text: block.text, level: block.type === 'h2' ? 2 : 3 });
      blocks.push({ ...block, id } as BlogPostBlockWithId);
    } else {
      blocks.push(block);
    }
  }
  return { toc: items, blocks };
};

const POSTS: BlogPost[] = [
  {
    slug: 'my-week-at-harvard-business-school-svmp',
    title: 'My Week at Harvard Business School: What SVMP Taught Me About Business and Leadership',
    author: 'Nicholas Pappas',
    category: 'Program Recap',
    coverImageSrc: svmpBanner,
    coverImageAlt: 'Harvard Business School SVMP banner',
    excerpt:
      'I flew to Boston as 1 of 180 college students selected for SVMP—and four days later, my perspective on leadership, decision-making, and business education had shifted.',
    content: [
      {
        type: 'p',
        text:
          "Last summer, I flew to Boston as 1 of 180 college students selected for the Summer Venture in Management Program at Harvard Business School. As a Computer Science and Engineering major who had never taken a single business class, I wasn't sure what to expect. I left four days later feeling like my entire perspective on leadership, decision-making, and my own potential had shifted."
      },
      { type: 'p', text: 'What follows is a candid recap of what SVMP was actually like and what I learned from it.' },
      { type: 'h2', text: 'What Is SVMP?' },
      {
        type: 'p',
        text:
          "The Summer Venture in Management Program is a four-day residential program at Harvard Business School designed to increase opportunity in business education for high-potential college students. It exposes participants to the case method, the same teaching style used in HBS's MBA program, and gives students a real taste of what business school looks and feels like."
      },
      {
        type: 'p',
        text:
          'It is geared toward college juniors and seniors who are making an impact in their school or community. All majors are welcome, and HBS covers all programmatic expenses including room and board.'
      },
      { type: 'h2', text: 'What the Experience Was Like' },
      {
        type: 'p',
        text:
          'The case method pushed me to think differently, speak up, and approach problems from multiple perspectives in ways I never had before as an engineering student. Professor John Beshears led the experience with clarity and passion, and I walked away with a much deeper understanding of how leadership, ethics, and decision-making intersect in the real world.'
      },
      {
        type: 'p',
        text:
          'But what stood out most were the people. Being surrounded by 179 other driven, mission-oriented students from across the country reminded me of the power of community. These were people doing remarkable things, leading organizations, serving their communities, and pushing themselves academically, and they were some of the most generous and collaborative people I have ever met.'
      },
      {
        type: 'p',
        text:
          'I also want to express my gratitude to donors Ian Loring and Isabelle Loring for making the program possible, to Cindy Chien for going over cases with me, and to Katya Gonzalez-Willette and Dana Fernandez for their incredible work behind the scenes.'
      },
      { type: 'h2', text: 'Why SVMP Matters' },
      {
        type: 'p',
        text:
          'When I got to SVMP, I quickly realized I was not the only one who had to fight to get there. Many of my fellow participants came from backgrounds where business school felt like a distant dream, not a realistic path. The program opened a door that most of us did not even know existed.'
      },
      {
        type: 'p',
        text:
          'Programs like SVMP matter because they make elite business education feel tangible much earlier. They give students real exposure to the case method, direct access to mentors, and the confidence to pursue opportunities that might have felt out of reach before.'
      },
      { type: 'h2', text: 'Apply for SVMP 2026' },
      {
        type: 'p',
        text:
          'If you are a college junior or senior making an impact in your school or community, I strongly encourage you to apply. The application deadline for SVMP 2026 is March 27, 2026, and the program runs June 14 through June 17 on HBS’s campus in Boston.'
      },
      {
        type: 'p',
        text:
          "You can apply at the HBS MBA Admissions portal. All majors are welcome. HBS covers room and board, and limited travel aid is available for admitted students who need it."
      },
      {
        type: 'p',
        text:
          'If you are considering applying, start early, be specific about your impact, and ask for feedback on your story from mentors who know your work well.'
      }
    ]
  },
  {
    slug: 'what-are-the-m7-business-schools-and-how-do-you-get-into-one',
    title: 'What Are the M7 Business Schools — And How Do You Get Into One?',
    author: 'Deferredly',
    category: 'Admissions Guide',
    coverImageSrc: m7Banner,
    coverImageAlt: 'M7 business schools header graphic',
    excerpt:
      "If you've spent any time researching MBA programs, you've probably seen the term \"M7\" thrown around—here's what it means and how to position yourself early.",
    content: [
      {
        type: 'p',
        text:
          "If you've spent any time researching MBA programs, you've probably seen the term \"M7\" thrown around. But if you're an undergrad just starting to explore business school, it can feel like an insider term nobody bothered to explain. So let's break it down."
      },
      { type: 'h2', text: 'What Is the M7?' },
      {
        type: 'p',
        text:
          'The M7 is an informal group of seven business schools widely considered the most prestigious MBA programs in the world. The name comes from a coalition the schools formed to share data and research on business education. The seven schools are:'
      },
      {
        type: 'ul',
        items: [
          'Harvard Business School (HBS)',
          'Stanford Graduate School of Business (GSB)',
          'Wharton School at the University of Pennsylvania',
          'Booth School of Business at the University of Chicago',
          'Kellogg SOM at Northwestern University',
          'MIT Sloan School of Management',
          'Columbia Business School'
        ]
      },
      {
        type: 'p',
        text:
          'These programs consistently rank at the top of every major MBA ranking, attract the most competitive applicants, and place graduates into the most sought-after roles in consulting, finance, tech, and entrepreneurship.'
      },
      { type: 'h2', text: 'Why Do People Care So Much About the M7?' },
      {
        type: 'p',
        text:
          'A few reasons. First, the networks are unmatched. When you graduate from an M7 program, you join an alumni base that spans nearly every major company, investment firm, and startup in the world. Those relationships open doors that are genuinely hard to open otherwise. Second, recruiters notice. The most competitive firms—think McKinsey, Goldman Sachs, Google, and top venture capital funds—recruit heavily and sometimes exclusively from M7 schools. The brand carries real weight. Third, for deferred MBA programs specifically, the M7 schools offer some of the most competitive and well-known options, including HBS 2+2, Stanford GSB Deferred Enrollment, Wharton Deferred Enrollment, Booth Scholars, Kellogg Future Leaders, Sloan Deferred, and Columbia Deferred Enrollment. These programs let you secure your MBA spot before you even graduate college—which is exactly what Deferredly is built to help you do.'
      },
      { type: 'h2', text: 'Who Actually Gets In?' },
      {
        type: 'p',
        text:
          'M7 admissions are genuinely competitive. Average GMAT scores typically sit above 730, average GPAs hover around 3.7, and most admitted students bring several years of high-impact work experience. At Stanford GSB, the acceptance rate is around 6%, making it one of the most selective graduate programs in the country.'
      },
      {
        type: 'p',
        text:
          'But numbers only tell part of the story. M7 schools are not just looking for the highest test scores in the room. They are looking for people who have led something, changed something, or built something. They want to understand who you are, what drives you, and why an MBA is the right next step for you specifically.'
      },
      {
        type: 'p',
        text:
          'For deferred programs, the profile shifts slightly. You are being evaluated earlier in your career, so schools lean more heavily on your potential—your trajectory, your leadership in college, your clarity of purpose, and your vision for what you want to do with the degree.'
      },
      { type: 'h2', text: 'What Should You Be Doing Right Now?' },
      {
        type: 'p',
        text:
          'If an M7 program is your goal, the work starts now, not in your late twenties. Here is what matters most as an undergrad:'
      },
      {
        type: 'ul',
        items: [
          'Build your GPA. A strong academic record signals that you can handle rigorous coursework.',
          'Take on real leadership. Titles matter less than impact. What did you actually change or build?',
          'Get meaningful work experience lined up. Internships at well-known firms or high-growth startups strengthen your profile considerably.',
          'Start thinking about your story. MBA applications are deeply personal. The students who get in know why they want to be there and can articulate it clearly.',
          'Learn about deferred programs. If you are a junior or senior, you may be eligible to apply now and lock in your spot before graduation. Deferredly was built specifically to help you navigate this process.'
        ]
      },
      { type: 'h2', text: 'The Bottom Line' },
      {
        type: 'p',
        text:
          'The M7 are the most competitive business programs in the world, but they are not out of reach if you start early, build intentionally, and tell a compelling story. The deferred MBA pathway exists precisely because these schools want to identify high-potential students before the traditional applicant pool even gets there.'
      },
      {
        type: 'p',
        text:
          'That is the opportunity. Deferredly is here to help you take it.'
      },
      {
        type: 'p',
        text:
          "Want to know if you're a competitive candidate for an M7 deferred program? Try Deferredly's free Profile Analysis tool and get personalized feedback on where you stand."
      }
    ]
  }
];

function renderBlock(block: BlogPostBlockWithId, idx: number) {
  switch (block.type) {
    case 'h2':
      return (
        <h2 key={idx} id={block.id} className="blog-post-h2">
          {block.text}
        </h2>
      );
    case 'h3':
      return (
        <h3 key={idx} id={block.id} className="blog-post-h3">
          {block.text}
        </h3>
      );
    case 'ul':
      return (
        <ul key={idx} className="blog-post-ul">
          {block.items.map((it) => (
            <li key={it}>{it}</li>
          ))}
        </ul>
      );
    case 'image': {
      const variantClass = block.variant === 'contain' ? 'contain' : 'full-width';
      return (
        <div key={idx} className={`blog-post-image-block ${variantClass}`}>
          <img className="blog-post-inline-image" src={block.src} alt={block.alt} />
        </div>
      );
    }
    case 'p':
    default:
      return (
        <p key={idx} className="blog-post-p">
          {block.text}
        </p>
      );
  }
}

const BlogPostView: React.FC<{ post: BlogPost }> = ({ post }) => {
  const { toc, blocks } = useMemo(() => normalizeContent(post.content), [post.content]);
  const [activeId, setActiveId] = useState<string>(toc[0]?.id ?? '');

  useEffect(() => {
    if (toc.length === 0) return;
    const ids = toc.map((t) => t.id);
    const els = ids.map((id) => document.getElementById(id)).filter(Boolean) as HTMLElement[];
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => (a.boundingClientRect.top ?? 0) - (b.boundingClientRect.top ?? 0));
        if (visible[0]?.target?.id) setActiveId(visible[0].target.id);
      },
      {
        root: null,
        rootMargin: '-20% 0px -70% 0px',
        threshold: [0, 1]
      }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [toc]);

  return (
    <div className="blog-post-layout">
      <aside className="blog-toc" aria-label="Article overview">
        <div className="blog-toc-card">
          <div className="blog-toc-title">Overview</div>
          <nav>
            <ul className="blog-toc-list">
              {toc.map((item) => (
                <li key={item.id} className={`blog-toc-item level-${item.level}`}>
                  <a
                    href={`#${item.id}`}
                    className={`blog-toc-link ${activeId === item.id ? 'active' : ''}`}
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById(item.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </aside>

      <article className="blog-post">
        <div className="blog-post-hero">
          <img className="blog-post-image" src={post.coverImageSrc} alt={post.coverImageAlt} />
        </div>

        <header className="blog-post-header">
          <div className="blog-post-category">{post.category}</div>
          <h1 className="blog-post-title">{post.title}</h1>
          <div className="blog-post-meta">By {post.author}</div>
        </header>

        <div className="blog-post-content">{blocks.map(renderBlock)}</div>
      </article>
    </div>
  );
};

const Blog: React.FC = () => {
  const params = useParams();
  const slug = params.slug;
  const post = slug ? POSTS.find((p) => p.slug === slug) : null;

  if (slug && !post) {
    return (
      <div className="blog-page">
        <div className="blog-container">
          <div className="blog-header">
            <h1 className="blog-title">Blog</h1>
            <p className="blog-subtitle">Stories, lessons, and guidance for deferred MBA applicants.</p>
          </div>
          <div className="blog-empty">
            Post not found. Go back to the <Link to="/blog">blog</Link>.
          </div>
        </div>
      </div>
    );
  }

  if (post) {
    return (
      <div className="blog-page">
        <div className="blog-container">
          <div className="blog-breadcrumb">
            <Link to="/blog" className="blog-backlink">
              ← Back to Blog
            </Link>
          </div>

          <BlogPostView post={post} />
        </div>
      </div>
    );
  }

  return (
    <div className="blog-page">
      <div className="blog-container">
        <div className="blog-header">
          <h1 className="blog-title">Blog</h1>
          <p className="blog-subtitle">Stories, lessons, and guidance for deferred MBA applicants.</p>
        </div>

        <div className="blog-grid">
          {POSTS.map((p) => (
            <Link key={p.slug} to={`/blog/${p.slug}`} className="blog-card">
              <div className="blog-card-media">
                <img className="blog-card-image" src={p.coverImageSrc} alt={p.coverImageAlt} />
              </div>
              <div className="blog-card-body">
                <div className="blog-card-category">{p.category}</div>
                <div className="blog-card-title">{p.title}</div>
                <div className="blog-card-meta">By {p.author}</div>
                <div className="blog-card-excerpt">{p.excerpt}</div>
                <div className="blog-card-cta">Read article →</div>
              </div>
            </Link>
          ))}
          <div className="blog-card blog-card-placeholder" aria-label="More articles coming soon">
            <div className="blog-card-placeholder-inner">
              <div className="blog-card-plus" aria-hidden="true">
                +
              </div>
              <div className="blog-card-placeholder-title">More articles coming soon</div>
              <div className="blog-card-placeholder-subtitle">
                Check back for more stories, guides, and tips for deferred MBA applicants.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Blog;
